import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useMe,
  useSubscriptionChange,
  useSubscriptionCheckout,
  useSubscriptionPlans,
  useSubscriptions,
} from "../api/api_hooks";
import { paymentsEnabled } from "../featureFlags";
import { useRemoteControl, useRemotePage } from "../remote/RemoteControlContext";
import { useRemoteSelectionScroll } from "../remote/useRemoteSelectionScroll";

const activeStatuses = new Set(["ACTIVE", "TRIALING"]);

function listFromResponse(data) {
  if (!data) return [];
  return Array.isArray(data) ? data : data.results || [];
}

function formatPrice(price) {
  if (!price) return "";
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: price.currency || "USD",
      maximumFractionDigits: 0,
    }).format(price.amount || 0);
  } catch {
    return `${price.amount} ${price.currency || ""}`.trim();
  }
}

function priceAmount(price) {
  return Number(price?.amount || 0);
}

function planFeatures(plan) {
  const sessions = plan.active_sessions_limit || 1;
  return [
    `${sessions} active ${sessions === 1 ? "session" : "sessions"}`,
    "Full access to all current and future games",
  ];
}

function PlanCard({
  plan,
  billingInterval,
  activePlan,
  pendingPlan,
  buttonLabel,
  buttonDisabled,
  pending,
  pendingLabel,
  onBuy,
  remoteFocused = false,
}) {
  const monthly = formatPrice(plan.price_per_month);
  const yearly = formatPrice(plan.price_per_year);
  const price = billingInterval === "month" ? monthly : yearly;
  const cadence = billingInterval === "month" ? "month" : "year";

  return (
    <article data-remote-focused={remoteFocused ? "true" : undefined} className={`group relative flex min-h-[460px] flex-col overflow-hidden rounded-3xl border bg-indigo-950/45 text-left shadow-xl shadow-slate-950/30 backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-sky-300/40 hover:shadow-sky-500/10 ${remoteFocused ? "-translate-y-1 border-cyan-300 ring-4 ring-cyan-300/20" : "border-white/10"}`}>
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-300 via-orange-300 to-pink-300 opacity-80" />
      {activePlan ? (
        <div className="absolute right-5 top-5 rounded-full border border-emerald-300/40 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-200">
          Active
        </div>
      ) : null}
      {!activePlan && pendingPlan ? (
        <div className="absolute right-5 top-5 rounded-full border border-yellow-300/40 bg-yellow-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-yellow-100">
          Scheduled
        </div>
      ) : null}

      <div className="h-44 overflow-hidden bg-slate-950">
        <img
          src={plan.image}
          alt={plan.name}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      <div className="flex flex-1 flex-col p-6">
      <div className="pr-24">
        <h2 className="text-2xl font-black tracking-[0.04em] text-white">{plan.name}</h2>
        <p className="mt-3 min-h-[72px] text-sm leading-6 text-indigo-100/75">{plan.description}</p>
      </div>

      <div className="mt-6 flex items-end gap-2">
        <span className="text-4xl font-black text-white">{price}</span>
        <span className="pb-1 text-sm font-semibold uppercase tracking-[0.22em] text-sky-200/80">
          / {cadence}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
          <dt className="text-xs uppercase tracking-[0.22em] text-indigo-200/70">Monthly</dt>
          <dd className="mt-1 font-semibold text-white">{monthly}</dd>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
          <dt className="text-xs uppercase tracking-[0.22em] text-indigo-200/70">Yearly</dt>
          <dd className="mt-1 font-semibold text-white">{yearly}</dd>
        </div>
      </dl>

      <ul className="mt-6 flex flex-1 flex-col gap-3 text-sm text-indigo-100/85">
        {planFeatures(plan).map((feature) => (
          <li key={feature} className="flex items-center gap-3">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-sky-300/15 text-sky-200">
              <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3.3 8.2l3 3.1 6.4-6.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => onBuy(plan)}
        disabled={buttonDisabled || pending}
        className="mt-8 rounded-full bg-pink-500 px-6 py-3 text-sm font-black uppercase tracking-[0.28em] text-white shadow-lg shadow-pink-950/30 transition hover:bg-pink-400 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-indigo-100/50 disabled:shadow-none"
      >
        {pending ? pendingLabel : buttonLabel}
      </button>
      </div>
    </article>
  );
}

function planAction(plan, activeSubscription, billingInterval) {
  if (!activeSubscription) {
    return paymentsEnabled
      ? { label: "Buy", type: "checkout", disabled: false }
      : { label: "Admin activation", type: "none", disabled: true };
  }

  const activePlan = activeSubscription.plan || {};
  const activeBillingInterval = activeSubscription.billing_interval || "month";
  const pendingPlanId = activeSubscription.pending_subscription_plan?.id;
  const pendingBillingInterval = activeSubscription.pending_billing_interval;

  if (pendingPlanId === plan.id && pendingBillingInterval === billingInterval) {
    return { label: "Scheduled", type: "none", disabled: true };
  }

  if (activePlan.id === plan.id && activeBillingInterval === billingInterval) {
    return { label: "Acquired", type: "none", disabled: true };
  }

  if (!paymentsEnabled) {
    return { label: "Admin activation", type: "none", disabled: true };
  }

  if (activePlan.id === plan.id && activeBillingInterval === "month" && billingInterval === "year") {
    return { label: "Upgrade to Yearly", type: "change", disabled: false };
  }

  if (activePlan.id === plan.id && activeBillingInterval === "year" && billingInterval === "month") {
    return { label: "Downgrade to Monthly", type: "change", disabled: false };
  }

  const planTierPrice = priceAmount(plan.price_per_month);
  const activeTierPrice = priceAmount(activePlan.price_per_month);
  if (planTierPrice > activeTierPrice) {
    return { label: "Upgrade", type: "change", disabled: false };
  }
  if (planTierPrice < activeTierPrice) {
    return { label: "Downgrade", type: "change", disabled: false };
  }

  return { label: "Change Plan", type: "change", disabled: false };
}

export default function BuySubscriptionPage() {
  const { remoteActive } = useRemoteControl();
  const [searchParams] = useSearchParams();
  const [billingInterval, setBillingInterval] = React.useState(
    searchParams.get("billing") === "year" ? "year" : "month"
  );
  const [selectedPlanId, setSelectedPlanId] = React.useState(null);
  const [focusedPlanIndex, setFocusedPlanIndex] = React.useState(0);
  const [remotePlanRequest, setRemotePlanRequest] = React.useState(null);
  const navigate = useNavigate();
  const { data: me } = useMe();
  const plansQuery = useSubscriptionPlans({ ordering: "name" });
  const subscriptionsQuery = useSubscriptions();
  const checkout = useSubscriptionCheckout();
  const subscriptionChange = useSubscriptionChange();

  const plans = listFromResponse(plansQuery.data);
  const subscriptions = listFromResponse(subscriptionsQuery.data);
  const activeSubscription = subscriptions.find(
    (subscription) =>
      Boolean(me?.id) &&
      subscription.user?.id === me.id &&
      activeStatuses.has(subscription.status)
  );
  const activePlanId = activeSubscription?.plan?.id;

  React.useEffect(() => {
    setFocusedPlanIndex((current) => Math.max(0, Math.min(current, plans.length - 1)));
  }, [plans.length]);

  const handleBuy = (plan) => {
    if (!paymentsEnabled) return;

    if (!me) {
      navigate("/login");
      return;
    }

    const action = planAction(plan, activeSubscription, billingInterval);
    if (action.disabled) return;

    setSelectedPlanId(plan.id);

    if (action.type === "change") {
      subscriptionChange.mutate(
        {
          planId: plan.id,
          billingInterval,
        },
        {
          onSettled: () => setSelectedPlanId(null),
        }
      );
      return;
    }

    const origin = window.location.origin;
    checkout.mutate(
      {
        planId: plan.id,
        billingInterval,
        successUrl: `${origin}/subscriptions?checkout=success`,
        cancelUrl: `${origin}/subscriptions?checkout=cancelled`,
      },
      {
        onSuccess: (data) => {
          if (data?.url) window.location.assign(data.url);
        },
        onSettled: () => setSelectedPlanId(null),
      }
    );
  };

  const focusedPlan = plans[focusedPlanIndex] || null;
  const planGridRef = useRemoteSelectionScroll(remoteActive, focusedPlan?.id ?? null);
  const movePlanFocus = (direction) => {
    if (!plans.length) return;
    setFocusedPlanIndex((current) => (current + direction + plans.length) % plans.length);
  };

  useRemotePage({
    id: "plans",
    title: "Membership plans",
    subtitle: focusedPlan?.name || "Choose your play pass",
    state: { billingInterval, focusedPlan: focusedPlan?.name || "", planCount: plans.length },
    sections: [{ title: "Plans", controls: [
      { id: "plans.billing", type: "select", label: "Billing", value: billingInterval, options: [{ value: "month", label: "Monthly" }, { value: "year", label: "Yearly" }] },
      { id: "plans.previous", type: "button", label: "Previous plan", disabled: !plans.length },
      { id: "plans.next", type: "button", label: "Next plan", disabled: !plans.length },
      { id: "plans.choose", type: "button", label: paymentsEnabled ? "Choose on main screen" : "Admin activation required", disabled: !focusedPlan || !paymentsEnabled },
    ] }],
    actions: {
      "plans.billing": ({ value }) => setBillingInterval(value === "year" ? "year" : "month"),
      "plans.previous": () => movePlanFocus(-1),
      "plans.next": () => movePlanFocus(1),
      "plans.choose": () => {
        if (paymentsEnabled) setRemotePlanRequest(focusedPlan);
      },
    },
  });

  return (
    <main className="flex-grow px-4 py-14">
      <section className="mx-auto max-w-6xl">
        <div className="flex flex-col items-center gap-5 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.45em] text-sky-200/80">
            Membership
          </p>
          <h1 className="max-w-3xl text-4xl font-black tracking-[0.04em] text-white sm:text-5xl">
            Choose your play pass
          </h1>
          <p className="max-w-2xl text-base leading-7 text-indigo-100/75">
            Pick a plan for the games, players, and sessions that fit your setup.
          </p>

          {!paymentsEnabled ? (
            <div className="max-w-2xl rounded-2xl border border-yellow-300/25 bg-yellow-300/10 px-5 py-3 text-sm font-semibold text-yellow-100">
              Online payments are disabled during the beta. Ask an administrator to activate the plan you need.
            </div>
          ) : null}

          <div className="mt-3 inline-flex rounded-full border border-white/10 bg-indigo-950/60 p-1 shadow-xl shadow-slate-950/30">
            {["month", "year"].map((interval) => (
              <button
                key={interval}
                type="button"
                onClick={() => setBillingInterval(interval)}
                className={`rounded-full px-5 py-2 text-xs font-black uppercase tracking-[0.28em] transition ${
                  billingInterval === interval
                    ? "bg-sky-300 text-slate-950"
                    : "text-indigo-100/70 hover:text-white"
                }`}
              >
                {interval === "month" ? "Monthly" : "Yearly"}
              </button>
            ))}
          </div>
        </div>

        {paymentsEnabled && searchParams.get("checkout") === "cancelled" ? (
          <div className="mx-auto mt-8 max-w-xl rounded-2xl border border-pink-300/25 bg-pink-500/10 px-5 py-3 text-sm text-pink-100">
            Checkout was cancelled. You can choose a plan whenever you are ready.
          </div>
        ) : null}
        {checkout.isError ? (
          <div className="mx-auto mt-8 max-w-xl rounded-2xl border border-pink-300/25 bg-pink-500/10 px-5 py-3 text-sm text-pink-100">
            {checkout.error?.response?.data?.detail || "Could not start Stripe checkout."}
          </div>
        ) : null}
        {subscriptionChange.isSuccess ? (
          <div className="mx-auto mt-8 max-w-xl rounded-2xl border border-emerald-300/25 bg-emerald-400/10 px-5 py-3 text-sm text-emerald-100">
            Your subscription change is scheduled for the end of the current billing period.
          </div>
        ) : null}
        {subscriptionChange.isError ? (
          <div className="mx-auto mt-8 max-w-xl rounded-2xl border border-pink-300/25 bg-pink-500/10 px-5 py-3 text-sm text-pink-100">
            {subscriptionChange.error?.response?.data?.detail || "Could not schedule subscription change."}
          </div>
        ) : null}

        {paymentsEnabled && remotePlanRequest ? (
          <div className="mx-auto mt-8 max-w-2xl rounded-3xl border border-cyan-300/30 bg-cyan-300/10 p-5 text-left shadow-xl">
            <h2 className="text-xl font-black text-white">Phone requested {remotePlanRequest.name}</h2>
            <p className="mt-2 text-sm text-indigo-100/70">Confirm on this screen before opening checkout or scheduling a subscription change.</p>
            <div className="mt-4 flex gap-3"><button type="button" onClick={() => { const plan = remotePlanRequest; setRemotePlanRequest(null); handleBuy(plan); }} className="rounded-xl bg-cyan-300 px-5 py-2.5 font-black text-slate-950">Confirm</button><button type="button" onClick={() => setRemotePlanRequest(null)} className="rounded-xl border border-white/15 px-5 py-2.5 font-bold text-white">Cancel</button></div>
          </div>
        ) : null}

        {plansQuery.isPending ? (
          <div className="mt-16 text-indigo-100/75">Loading subscription plans...</div>
        ) : plansQuery.isError ? (
          <div className="mt-16 text-pink-200">Could not load subscription plans.</div>
        ) : (
          <div ref={planGridRef} className="mt-12 grid grid-cols-1 justify-center gap-7 md:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(0,24rem))]">
            {plans.map((plan, index) => {
              const action = planAction(plan, activeSubscription, billingInterval);
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  billingInterval={billingInterval}
                  activePlan={activePlanId === plan.id}
                  pendingPlan={activeSubscription?.pending_subscription_plan?.id === plan.id}
                  buttonLabel={action.label}
                  buttonDisabled={action.disabled}
                  pending={(checkout.isPending || subscriptionChange.isPending) && selectedPlanId === plan.id}
                  pendingLabel={action.type === "checkout" ? "Opening..." : "Scheduling..."}
                  onBuy={handleBuy}
                  remoteFocused={remoteActive && index === focusedPlanIndex}
                />
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
